import axios from 'axios';
import {
    API_BASE_URL,
    getAuthHeaders, 
    getRefreshToken,
    clearSession 
} from './constants';
import { notifyError } from '../utils/notifications';

/** 
 * Cliente axios configurado con baseURL y timeout
*/
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000, // 15 segundos
});

/**
 * Flag para prevenir múltiples refresh simultáneos
 */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });

    failedQueue = [];
};

/** 
 * INTERCEPTOR DE REQUEST
 * Añade automáticamente el token de autenticación a cada petición
*/
apiClient.interceptors.request.use(
    (config) => {
        // Aquí se añaden los headers de la autenticación
        const authHeaders = getAuthHeaders();
        config.headers = {
            ...config.headers,
            ...authHeaders
        };

        return config;
    }, 
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * INTERCEPTOR DE RESPONSE
 * Maneja errores globales y refresh de token automático
 */
apiClient.interceptors.response.use(
    (response) => {
        // Si es exitosa que simplemente la retorne
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Error 401 de expiración de token
        if (error.response?.status === 401 && !originalRequest._retry) {
            if(isRefreshing) {
                // Si ya existe un refresh, que se espere
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => {
                    return apiClient(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = getRefreshToken();

            if (!refreshToken) {
                // De no haber refresh token, se redirecciona a login
                clearSession();
                window.location.href = '/login';
                notifyError('Sesión expirada. Por favor, inicia sesión nuevamente.');
                return Promise.reject(error);
            }

            try {
                // Intentar refrescar el token
                const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                    refresh_token: refreshToken
                });

                if (response.data?.session?.access_token) {
                    // Actualizar el nuevo token
                    localStorage.setItem('authToken', response.data.session.access_token);

                    // Actualizar expiración si viene en la respuesta
                    if (response.data.session.expires_in) {
                        const expiryTime = Date.now() + (response.data.session.expires_in * 1000);
                        localStorage.setItem('sessionExpiry', expiryTime.toString());
                    }

                    processQueue(null, response.data.session.access_token);

                    // Reintentar la petición original con el nuevo token
                    originalRequest.headers['Authorization'] = `Bearer ${response.data.session.access_token}`;
                    return apiClient(originalRequest);
                }
            } catch (refreshError) {
                // Hasta aquí quiere decir que Refresh falló asi que cerramos sesión
                processQueue(refreshError, null);
                clearSession();
                window.location.href = '/login';
                notifyError('Sesión expirada. Por favor, inicia sesión nuevamente.');
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Error 403 de falta de permisos
        if (error.response?.status === 403) {
            notifyError('No tienes permisos para realizar esta acción.');
        }

        // Errores de conexión
        if(!error.response) {
            notifyError('Error de conexión. Verifica tu conexión a internet.');
            return Promise.reject(error);
        }

        // Errores epecíficos de la API
        if (error.response?.data?.error) {
            const apiError = error.response.data;

            // Errores basados en la documentación
            switch (apiError.error) {
                case 'PARAMETERS_NOT_PRESENT':
                    notifyError(apiError.message || 'Faltan parámetros requeridos');
                    break;
                case 'NotAuthorizedException':
                    notifyError(apiError.message || 'Credenciales inválidas');
                    break;
                case 'INVALID_REFRESH_TOKEN':
                    clearSession();
                    window.location.href = '/login';
                    notifyError('Sesión expirada. Por favor, inicia sesión nuevamente.');
                    break;
                default:
                    // Error genérico de API
                    notifyError(apiError.message || 'Ocurrió un error en el servidor.');
            }
        }

        // Retornamos el error para el manejo específico en servicios
        return Promise.reject(error);
    }
);

export default apiClient;